import React, { useEffect, useState } from "react";
import { MdLinkOff } from "react-icons/md";
import {
  InformationCircle,
  CheckCircle,
} from "../../../../components/icons/svg/outline";
import { updateShiftField } from "../../../../utils";
import ConflictTooltip from "../../../../components/common/tooltip/ConflictTooltip";
import { getJobNumbers } from "../../../../services/api/core";

/**
 * JobColumn component for displaying job number with conflict detection
 * Handles both editing and non-editing modes with conflict tooltips and dropdown selection
 */
const JobColumn = ({
  shift,
  isEditing,
  isReopenedReview = false,
  bgColor,
  dayIndex,
  shiftIndex,
  setShiftsToRender,
  date, // Date for the shift (YYYY-MM-DD format)
  className = "",
}) => {
  // Fetch jobs for the specific date (not just today)
  const [jobsList, setJobsList] = useState([]);
  
  // Fetch jobs for the shift's date whenever date changes
  useEffect(() => {
    const fetchJobsForDate = async () => {
      if (date) {
        try {
          const jobs = await getJobNumbers(true, date);
          setJobsList(jobs);
        } catch (error) {
          console.error('Error fetching jobs for date:', error);
          setJobsList([]);
        }
      } else {
        // If no date provided, fetch for today as fallback
        try {
          const jobs = await getJobNumbers(true);
          setJobsList(jobs);
        } catch (error) {
          console.error('Error fetching jobs:', error);
          setJobsList([]);
        }
      }
    };
    fetchJobsForDate();
  }, [date]);

  // Get values from shift
  const currentJob = shift.job;
  const hoursLogJob = shift.jobHoursLog;
  const payoutJob = shift.jobPayout;

  // Check if this is a payout-added shift (no id)
  const isPayoutAddedShift = shift.id === null || shift.id === undefined;

  // Detect conflicts (only if not a payout-added shift)
  const hasConflict = !isPayoutAddedShift && currentJob !== hoursLogJob;
  const hasPayoutConflict = payoutJob !== currentJob;

  // Handle job selection changes
  const handleJobChange = (e) => {
    const newJobId = parseInt(e.target.value);
    setShiftsToRender((prevData) =>
      updateShiftField(prevData, dayIndex, shiftIndex, "jobPayout", newJobId)
    );
  };

  // Handle job selection from conflict tooltip
  const handleJobSelect = (selectedJobId) => {
    setShiftsToRender((prevData) =>
      updateShiftField(
        prevData,
        dayIndex,
        shiftIndex,
        "jobPayout",
        selectedJobId
      )
    );
  };

  // Get job number by ID
  const getJobNumber = (jobId) => {
    if (!jobId) return "";
    const job = jobsList.find((j) => j.id === jobId);
    return job ? job.number : "Job not active";
  };

  // Get job description by ID
  const getJobDescription = (jobId) => {
    const job = jobsList.find((j) => j.id === jobId);
    return job ? job.description : "Unknown Job";
  };

  // Set default job for payout-added shifts if no job is selected
  useEffect(() => {
    if (isPayoutAddedShift && !payoutJob && jobsList.length > 0) {
      setShiftsToRender((prevData) =>
        updateShiftField(prevData, dayIndex, shiftIndex, "jobPayout", jobsList[0].id)
      );
    }
  }, [isPayoutAddedShift, payoutJob, jobsList, dayIndex, shiftIndex, setShiftsToRender]);

  // Get text color class based on conflicts
  const getTextColorClass = () => {
    if (hasPayoutConflict) {
      return "text-blue-600";
    }
    if (!isPayoutAddedShift && !hasConflict && hoursLogJob && isEditing) {
      return "text-black";
    }
    return "";
  };

  // Render conflict/status icons for editing mode
  const renderStatusIcon = () => {
    if (!isEditing) return null;

    // Hide hours log related icons for payout-added shifts
    if (isPayoutAddedShift) return null;

    if (hasConflict) {
      return shift.missingLogs ? (
        <InformationCircle className="h-4 lg:text-lg translate-x-[2px] text-yellow-600" />
      ) : (
        <MdLinkOff className="h-4 text-lg translate-x-1 text-red-600" />
      );
    }

    if (hoursLogJob && isEditing) {
      return <CheckCircle className="h-4 lg:text-lg text-green-600" />;
    }

    return null;
  };

  const cellClasses = `col-span-2 ${bgColor} py-3 px-1 border-b border-l border-gray-300 flex items-center justify-center text-sm h-12 ${isEditing ? "bg-white" : ""
    } relative group ${className}`;

  return (
    <div className={cellClasses}>
      {isEditing ? (
        <div className="flex items-center justify-center w-full relative">
          {renderStatusIcon()}
          <div className="relative w-full">
            {/* This custom select is styled to hide the description in the upper level (cell) but not in the dropdown */}
            <select
              value={payoutJob || ""}
              className={`w-full h-8 text-center border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-300 rounded cursor-pointer`}
              style={{ color: "transparent" }}
              onChange={handleJobChange}
            >
              {jobsList.map((job) => (
                <option
                  key={job.id}
                  value={job.id}
                  className="text-left"
                  style={{ color: "black" }}
                >
                  {job.number} - {job.description}
                </option>
              ))}
            </select>
            {/* Custom display for selected value */}
            {payoutJob ? (
              <div
                className={`absolute inset-0 flex items-center justify-center pointer-events-none text-center ${getTextColorClass()}`}
              >
                {getJobNumber(payoutJob)}
              </div>
            ) : ( // If no job is selected, show the first job (will be set via useEffect)
              <div
                className={`absolute inset-0 flex items-center justify-center pointer-events-none text-center text-blue-600`}
              >
                {jobsList.length > 0 ? getJobNumber(jobsList[0].id) : "No Jobs"}
              </div>
            )}
          </div>
        </div>
      ) : (
        <span
          id={`job-${dayIndex}-${shiftIndex}`}
          className={`text-center ${getTextColorClass()}`}
        >
          {getJobNumber(payoutJob)}
        </span>
      )}

      {/* Job Description Tooltip - Always visible on hover */}
      <div className="absolute z-10 bg-black text-white px-2 py-1 rounded text-xs whitespace-nowrap top-12 left-1/2 transform -translate-x-1/2 opacity-0 invisible delay-100 group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
        {getJobDescription(payoutJob)}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-black"></div>
      </div>

      {/* Conflict Tooltip */}
      {!isPayoutAddedShift ? (
        <ConflictTooltip
          isReopenedReview={isReopenedReview}
          shift={shift}
          fieldType="job"
          isEditing={isEditing}
          dayIndex={dayIndex}
          shiftIndex={shiftIndex}
          onValueSelect={isEditing ? handleJobSelect : null}
          jobsList={jobsList}
        />
      ) : null}
    </div>
  );
};

export default JobColumn;
